import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { mmss } from "@/lib/format";
import { cn } from "@/lib/utils";
import { VegDot } from "@/components/common/Bits";
import {
  ChefHat,
  Clock,
  ChevronRight,
  Check,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const STATIONS = ["all", "Main Kitchen", "Tandoor", "Bar", "Bakery"];

const COLUMNS = [
  {
    key: "new",
    label: "NEW",
    head: "bg-sky-600",
    ring: "border-sky-200",
    next: "preparing",
    cta: "Start",
  },
  {
    key: "preparing",
    label: "PREPARING",
    head: "bg-amber-500",
    ring: "border-amber-200",
    next: "ready",
    cta: "Ready",
  },
  {
    key: "ready",
    label: "READY",
    head: "bg-emerald-600",
    ring: "border-emerald-200",
    next: "completed",
    cta: "Served",
  },
];

export default function Kitchen() {
  const { branchId, branches, subscribe } = useApp();

  const activeBranch = branchId === "all" ? branches[0]?.id : branchId;

  const [kots, setKots] = useState([]);
  const [station, setStation] = useState("all");
  const [, setTick] = useState(0);

  const load = () => {
    if (!activeBranch) return;

    api
      .kots({
        branch_id: activeBranch,
        active: true,
      })
      .then((r) => setKots(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, [activeBranch]);

  useEffect(() => {
    const subs = ["kot.new", "kot.update", "order.update"].map((event) =>
      subscribe(event, load),
    );

    return () => subs.forEach((unsubscribe) => unsubscribe());
  }, [subscribe, activeBranch]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick((x) => x + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const filtered = useMemo(
    () =>
      kots.filter(
        (k) => station === "all" || k.station === station,
      ),
    [kots, station],
  );

  const move = async (kot, next) => {
    try {
      await api.setKotStatus(kot.id, next);

      if (next !== "completed") {
        toast.success(`KOT #${kot.kot_no} → ${next}`);
      }

      load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-stone-900">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white">
          <ChefHat size={22} className="text-primary" />

          <h1 className="font-display text-xl font-bold">
            Kitchen Display
          </h1>

          <span className="text-stone-500 text-sm">
            · {filtered.length} active tickets
          </span>
        </div>

        {/* Stations */}
        <div className="flex gap-1.5">
          {STATIONS.map((s) => (
            <button
              key={s}
              onClick={() => setStation(s)}
              data-testid={`kds-station-${s}`}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors",
                station === s
                  ? "bg-primary text-white"
                  : "bg-white/5 text-stone-300 hover:bg-white/10",
              )}
            >
              {s === "all" ? "All Stations" : s}
            </button>
          ))}
        </div>
      </div>

      {/* Main KDS Area */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {filtered.length === 0 ? (
          /* Empty State */
          <div
            className="h-full flex items-center justify-center px-6"
            data-testid="kds-empty-state"
          >
            <div className="flex flex-col items-center text-center max-w-sm">
              <div className="h-16 w-16 rounded-2xl bg-white/5 flex items-center justify-center mb-5">
                <ClipboardList
                  size={30}
                  strokeWidth={1.8}
                  className="text-stone-500"
                />
              </div>

              <h2 className="font-display text-lg font-bold text-white">
                No active kitchen tickets
              </h2>

              <p className="text-sm text-stone-500 mt-2 leading-relaxed">
                New orders sent from POS will appear here for the kitchen
                team.
              </p>
            </div>
          </div>
        ) : (
          /* KDS Columns */
          <div className="h-full grid grid-cols-1 md:grid-cols-3 gap-4 p-4 overflow-hidden">
            {COLUMNS.map((col) => {
              const cards = filtered.filter(
                (k) => k.status === col.key,
              );

              return (
                <div
                  key={col.key}
                  className="flex flex-col min-h-0"
                  data-testid={`kds-col-${col.key}`}
                >
                  {/* Column Header */}
                  <div
                    className={cn(
                      "rounded-t-xl px-4 py-2.5 flex items-center justify-between text-white font-display font-bold tracking-wide",
                      col.head,
                    )}
                  >
                    {col.label}

                    <span className="h-6 min-w-6 px-1.5 rounded-full bg-white/25 grid place-items-center text-sm">
                      {cards.length}
                    </span>
                  </div>

                  {/* Column Content */}
                  <div className="flex-1 overflow-y-auto space-y-3 p-2 bg-white/[0.03] rounded-b-xl">
                    {cards.map((k) => {
                      const late =
                        (Date.now() -
                          new Date(k.created_at).getTime()) /
                          60000 >
                        12;

                      return (
                        <div
                          key={k.id}
                          data-testid={`kot-card-${k.id}`}
                          className={cn(
                            "rounded-xl bg-white p-3.5 border-l-4",
                            late && col.key !== "ready"
                              ? "border-red-500"
                              : "border-transparent",
                            late &&
                              col.key === "new" &&
                              "ring-2 ring-red-400",
                          )}
                        >
                          {/* Order Header */}
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-display text-lg font-bold">
                                #{k.order_no}
                              </span>

                              <span className="ml-2 text-sm font-semibold text-stone-500">
                                {k.table_name}
                              </span>
                            </div>

                            <div
                              className={cn(
                                "flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-bold tabular-nums",
                                late
                                  ? "bg-red-100 text-red-600"
                                  : "bg-stone-100 text-stone-600",
                              )}
                            >
                              <Clock size={13} />
                              {mmss(k.created_at)}
                            </div>
                          </div>

                          {/* Station */}
                          <div className="text-[10px] font-bold uppercase tracking-widest text-primary mt-1">
                            {k.station}
                          </div>

                          {/* Items */}
                          <div className="mt-2.5 space-y-1.5">
                            {k.items.map((it, i) => (
                              <div
                                key={i}
                                className="flex items-center gap-2"
                              >
                                <VegDot
                                  veg={it.veg}
                                  className="h-3.5 w-3.5"
                                />

                                <span className="font-display text-base font-bold text-stone-800">
                                  {it.qty}×
                                </span>

                                <span className="text-base font-medium text-stone-700 flex-1 leading-tight">
                                  {it.name}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Note */}
                          {k.note && (
                            <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-sm font-semibold text-amber-700">
                              Note: {k.note}
                            </div>
                          )}

                          {/* Action */}
                          <button
                            onClick={() => move(k, col.next)}
                            data-testid={`kot-advance-${k.id}`}
                            className={cn(
                              "mt-3 w-full rounded-xl py-2.5 font-display font-bold text-white flex items-center justify-center gap-1.5 transition-transform active:scale-95",
                              col.key === "ready"
                                ? "bg-stone-800 hover:bg-stone-700"
                                : "bg-primary hover:bg-[#C2410C]",
                            )}
                          >
                            {col.key === "ready" ? (
                              <Check size={17} />
                            ) : (
                              <ChevronRight size={17} />
                            )}

                            {col.cta}
                          </button>
                        </div>
                      );
                    })}

                    {/* Empty column state only when other tickets exist */}
                    {cards.length === 0 && (
                      <div className="h-full min-h-[160px] flex items-center justify-center">
                        <span className="text-stone-600 text-sm">
                          No tickets
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}